import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faPencil,
  faTrash,
  faDollarSign,
  faChevronDown,
  faChevronRight,
  faEye,
  faEyeSlash,
} from '@fortawesome/free-solid-svg-icons';
import api from '../services/api';

const SurfacePrepMethodsManagement = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('methods'); // 'methods' or 'blended'
  
  // Methods tab state
  const [methods, setMethods] = useState([]); // Now grouped by year: [{year: 2024, methods: [...], currency: 'CAD', unitOfMeasure: 'sqm'}, ...]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [expandedYears, setExpandedYears] = useState(new Set()); // Track which years are expanded
  const [editingYear, setEditingYear] = useState(null); // For year-level editing
  
  // Blended costs tab state
  const [blendedCosts, setBlendedCosts] = useState([]);
  const [blendedLoading, setBlendedLoading] = useState(false);
  const [blendedError, setBlendedError] = useState('');
  const [showBlendedModal, setShowBlendedModal] = useState(false);
  const [combinations, setCombinations] = useState([]);
  const [surfacePrepMethods, setSurfacePrepMethods] = useState([]); // For cost calculation
  const [blendedFilters, setBlendedFilters] = useState({
    year: '',
    is_active: ''
  });
  const [blendedPagination, setBlendedPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [formData, setFormData] = useState({
    method_code: '',
    method_name: '',
    cost_per_unit: '',
    currency: 'CAD',
    unit_of_measure: 'sqm',
    year: 2024,
    sort_order: 99.0,
    });
  const [convertedCost, setConvertedCost] = useState(null);
  const [defaultSystemSettings, setDefaultSystemSettings] = useState({ currency: 'CAD', uom: 'sqm' });
  
  // Blended cost form data (costs keyed by combination code from API)
  const [blendedFormData, setBlendedFormData] = useState({
    year: new Date().getFullYear(),
    costs: {},
    blendPercentages: {},
    currency: 'CAD',
    unitOfMeasure: 'sqm',
    isActive: true
  });
  const [calculatedBlendedCost, setCalculatedBlendedCost] = useState(0);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [yearFilter, setYearFilter] = useState('');

  // Derived: combination codes for table and form (dynamic from API)
  const combinationCodes = useMemo(() =>
    combinations.map(c => c.code).filter(Boolean),
    [combinations]
  );
  // Derived: SP method codes for blend percentages (dynamic from surface prep methods)
  const spMethodCodes = useMemo(() =>
    [...new Set(surfacePrepMethods.map(m => m.methodCode || m.method_code))].filter(Boolean).sort(),
    [surfacePrepMethods]
  );

  // Fetch default system settings and conversion rate
  useEffect(() => {
    const fetchDefaultSettings = async () => {
      try {
        // Get default currency and UOM from settings
        const settingsResponse = await api.get('/settings/categories');
        if (settingsResponse.data.success) {
          const settings = settingsResponse.data.data;
          const currency = settings.currency_units?.currency?.setting_value || 'CAD';
          const preferredUnits = settings.currency_units?.preferred_units?.setting_value || 'm²';
          const uom = preferredUnits.toLowerCase().includes('m') ? 'sqm' : 'sqft';
          setDefaultSystemSettings({ currency, uom });
        }
      } catch (err) {
        console.error('Error fetching default settings:', err);
      }
    };
    fetchDefaultSettings();
  }, []);

  // Calculate converted cost when form data changes
  useEffect(() => {
    const calculateConvertedCost = async () => {
      if (!formData.cost_per_unit || !formData.currency || !formData.unit_of_measure || !formData.year) {
        setConvertedCost(null);
        return;
      }

      try {
        // Get conversion rate for the year
        let currencyRate = 1.0;
        if (formData.currency !== defaultSystemSettings.currency) {
          const rateResponse = await api.get(`/currency-conversion-rates/year/${formData.year}/${formData.currency}/${defaultSystemSettings.currency}`);
          if (rateResponse.data.success) {
            currencyRate = parseFloat(rateResponse.data.data.conversionRate);
          }
        }

        // Convert currency
        let converted = parseFloat(formData.cost_per_unit) * currencyRate;

        // Convert UOM (1 sqft = 0.092903 sqm)
        const SQFT_TO_SQM = 0.092903;
        if (formData.unit_of_measure === 'sqft' && defaultSystemSettings.uom === 'sqm') {
          converted = converted * SQFT_TO_SQM;
        } else if (formData.unit_of_measure === 'sqm' && defaultSystemSettings.uom === 'sqft') {
          converted = converted / SQFT_TO_SQM;
        }

        setConvertedCost(converted.toFixed(2));
      } catch (err) {
        console.error('Error calculating converted cost:', err);
        setConvertedCost(null);
      }
    };

    calculateConvertedCost();
  }, [formData.cost_per_unit, formData.currency, formData.unit_of_measure, formData.year, defaultSystemSettings]);

  // Fetch methods (grouped by year)
  const fetchMethods = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        grouped_by_year: 'true',
        ...(searchTerm && { search: searchTerm }),
        ...(yearFilter && { year: yearFilter }),
      });

      const response = await api.get(`/surface-prep-methods?${params}`);
      if (response.data.success) {
        setMethods(response.data.data);
        setPagination(response.data.pagination);
        // Auto-expand first year if any
        if (response.data.data.length > 0 && expandedYears.size === 0) {
          setExpandedYears(new Set([response.data.data[0].year]));
        }
      }
    } catch (err) {
      setError('Failed to fetch surface preparation methods');
      console.error('Error fetching methods:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Toggle year expansion
  const toggleYearExpansion = (year) => {
    setExpandedYears(prev => {
      const newSet = new Set(prev);
      if (newSet.has(year)) {
        newSet.delete(year);
      } else {
        newSet.add(year);
      }
      return newSet;
    });
  };
  
  // Import previous year's methods
  const handleImportPreviousYear = async (targetYear) => {
    // Find the most recent year before targetYear
    const previousYearData = methods
      .filter(m => m.year < targetYear)
      .sort((a, b) => b.year - a.year)[0];
    
    if (!previousYearData) {
      alert('No previous year data found to import.');
      return;
    }
    
    if (!window.confirm(`Import all methods from ${previousYearData.year} to ${targetYear}?`)) {
      return;
    }
    
    try {
      // Create methods for target year based on previous year
      const importPromises = previousYearData.methods.map(method => {
        // Handle both camelCase (from API) and snake_case (from direct object access)
        const methodCode = method.methodCode || method.method_code;
        const methodName = method.methodName || method.method_name;
        // Ensure numeric values are properly converted
        const costPerUnit = parseFloat(method.costPerUnit !== undefined ? method.costPerUnit : (method.costPerUnitCad !== undefined ? method.costPerUnitCad : method.cost_per_unit_cad)) || 0;
        const currency = method.currency || 'CAD';
        const unitOfMeasure = method.unitOfMeasure || method.unit_of_measure || 'sqm';
        const sortOrder = parseFloat(method.sortOrder !== undefined ? method.sortOrder : (method.sort_order !== undefined ? method.sort_order : 99.0)) || 99.0;
        const isActive = method.isActive !== undefined ? method.isActive : (method.is_active !== undefined ? method.is_active : true);
        
        return api.post('/surface-prep-methods', {
          method_code: methodCode,
          method_name: methodName,
          cost_per_unit: costPerUnit, // Ensure it's a number
          currency: currency,
          unit_of_measure: unitOfMeasure,
          year: targetYear,
          sort_order: sortOrder, // Ensure it's a number
          is_active: isActive
        });
      });
      
      const results = await Promise.allSettled(importPromises);
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected');
      
      if (failed.length > 0) {
        console.error('Failed imports:', failed);
        const errorMessages = failed.map((f, idx) => {
          const method = previousYearData.methods[idx];
          return `${method.methodCode || method.method_code}: ${f.reason?.response?.data?.message || f.reason?.message || 'Unknown error'}`;
        }).join('\n');
        alert(`Imported ${successful} of ${previousYearData.methods.length} methods.\n\nFailed:\n${errorMessages}`);
      } else {
        alert(`Successfully imported ${successful} methods from ${previousYearData.year} to ${targetYear}`);
      }
      
      await fetchMethods();
    } catch (err) {
      alert('Failed to import previous year methods: ' + (err.response?.data?.message || err.message));
      console.error('Error importing methods:', err);
    }
  };

  // Fetch surface prep methods for cost calculation
  const fetchSurfacePrepMethodsForCalculation = async () => {
    try {
      const response = await api.get('/surface-prep-methods?limit=100&is_active=true');
      if (response.data.success) {
        setSurfacePrepMethods(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching surface prep methods for calculation:', err);
    }
  };

  // Fetch combinations for blended costs
  const fetchCombinations = async () => {
    try {
      const response = await api.get('/blended-surface-prep/combinations');
      if (response.data.success) {
        setCombinations(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching combinations:', err);
    }
  };

  // Fetch blended costs (grouped by year)
  const fetchBlendedCosts = async () => {
    setBlendedLoading(true);
    setBlendedError('');
    try {
      const params = new URLSearchParams({
        page: blendedPagination.currentPage.toString(),
        limit: '20'
      });

      if (blendedFilters.year) params.append('year', blendedFilters.year);
      if (blendedFilters.is_active !== '') params.append('is_active', blendedFilters.is_active);

      const response = await api.get(`/blended-surface-prep?${params}`);

      if (response.data.success) {
        setBlendedCosts(response.data.data);
        setBlendedPagination(response.data.pagination);
      }
    } catch (err) {
      setBlendedError(err.response?.data?.message || 'Failed to fetch blended costs');
    } finally {
      setBlendedLoading(false);
    }
  };

  // Calculate blended cost based on percentages and method costs
  const calculateBlendedCost = (percentages, year) => {
    if (!percentages || Object.keys(percentages).length === 0) {
      return 0;
    }

    // Get methods for the specified year
    const methodsForYear = surfacePrepMethods.filter(m => m.year === year && m.isActive);
    if (methodsForYear.length === 0) {
      return 0;
    }

    let totalCost = 0;
    for (const [methodCode, percentage] of Object.entries(percentages)) {
      const method = methodsForYear.find(m => m.methodCode === methodCode);
      if (method && percentage > 0) {
        // Use cost_per_unit (in selected currency)
        const cost = parseFloat(method.costPerUnit !== undefined ? method.costPerUnit : (method.costPerUnitCad || 0)) || 0;
        totalCost += cost * (parseFloat(percentage) / 100);
      }
    }

    return parseFloat(totalCost.toFixed(2));
  };

  useEffect(() => {
    if (activeTab === 'methods') {
      fetchMethods();
    } else if (activeTab === 'blended') {
      fetchBlendedCosts();
      fetchCombinations();
      fetchSurfacePrepMethodsForCalculation();
    }
  }, [activeTab, pagination.page, searchTerm, yearFilter, blendedPagination.currentPage, blendedFilters]);

  // Recalculate blended cost when percentages or year changes
  useEffect(() => {
    if (activeTab === 'blended' && showBlendedModal && Object.keys(blendedFormData.blendPercentages).length > 0 && surfacePrepMethods.length > 0) {
      const calculated = calculateBlendedCost(blendedFormData.blendPercentages, blendedFormData.year);
      setCalculatedBlendedCost(calculated);
      if (calculated > 0) {
        setBlendedFormData(prev => ({ ...prev, blendedCostCAD: calculated.toString() }));
      }
    } else if (showBlendedModal && Object.keys(blendedFormData.blendPercentages).length === 0) {
      setCalculatedBlendedCost(0);
    }
  }, [blendedFormData.blendPercentages, blendedFormData.year, showBlendedModal, surfacePrepMethods]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMethod) {
        await api.put(`/surface-prep-methods/${editingMethod.id}`, formData);
      } else {
        await api.post('/surface-prep-methods', formData);
      }
      
      setShowModal(false);
      setEditingMethod(null);
      setFormData({
        method_code: '',
        method_name: '',
        cost_per_unit: '',
        currency: 'CAD',
        unit_of_measure: defaultSystemSettings.uom,
        year: 2024,
        sort_order: 99.0,
        });
      fetchMethods();
    } catch (err) {
      setError('Failed to save surface preparation method');
      console.error('Error saving method:', err);
    }
  };

  // Handle edit
  const handleEdit = (method) => {
    setEditingMethod(method);
    setEditingYear(method.year);
    setFormData({
      method_code: method.methodCode,
      method_name: method.methodName,
      cost_per_unit: method.costPerUnit !== undefined ? method.costPerUnit : (method.costPerUnitCad || 0),
      unit_of_measure: method.unitOfMeasure,
      year: method.year,
      currency: method.currency,
      is_active: method.isActive,
      sort_order: method.sortOrder || 99.0
    });
    setShowModal(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this surface preparation method?')) {
      try {
        await api.delete(`/surface-prep-methods/${id}`);
        fetchMethods();
      } catch (err) {
        setError('Failed to delete surface preparation method');
        console.error('Error deleting method:', err);
      }
    }
  };

  

  // Reset form
  const resetForm = () => {
      setFormData({
        method_code: '',
        method_name: '',
        cost_per_unit: '',
        currency: 'CAD',
        unit_of_measure: 'sqm',
        year: 2024,
        sort_order: 99.0,
        });
      setConvertedCost(null);
      setEditingMethod(null);
  };

  // Blended cost handlers
  const handleBlendedFilterChange = (e) => {
    const { name, value } = e.target;
    setBlendedFilters(prev => ({ ...prev, [name]: value }));
    setBlendedPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const resetBlendedForm = () => {
    const defaultCosts = combinationCodes.length > 0
      ? combinationCodes.reduce((acc, code) => ({ ...acc, [code]: 0 }), {})
      : {};
    const defaultBlend = spMethodCodes.length > 0
      ? spMethodCodes.reduce((acc, code) => ({ ...acc, [code]: 0 }), {})
      : {};
    setBlendedFormData({
      year: new Date().getFullYear(),
      costs: defaultCosts,
      blendPercentages: defaultBlend,
      currency: 'CAD',
      unitOfMeasure: defaultSystemSettings.uom,
      isActive: true
    });
    setCalculatedBlendedCost(0);
    setEditingYear(null);
  };

  const handleBlendedSubmit = async (e) => {
    e.preventDefault();
    setBlendedLoading(true);
    setBlendedError('');

    try {
      if (editingYear) {
        await api.put(`/blended-surface-prep/year/${editingYear}`, blendedFormData);
      } else {
        await api.post('/blended-surface-prep', blendedFormData);
      }
      
      setShowBlendedModal(false);
      resetBlendedForm();
      fetchBlendedCosts();
    } catch (err) {
      setBlendedError(err.response?.data?.message || 'Failed to save blended costs');
    } finally {
      setBlendedLoading(false);
    }
  };

  const handleBlendedEdit = async (yearData) => {
    try {
      const response = await api.get(`/blended-surface-prep/year/${yearData.year}`);
      if (response.data.success) {
        const data = response.data.data;
        const fallbackCosts = combinationCodes.length > 0
          ? combinationCodes.reduce((acc, code) => ({ ...acc, [code]: 0 }), {})
          : {};
        const fallbackBlend = spMethodCodes.length > 0
          ? spMethodCodes.reduce((acc, code) => ({ ...acc, [code]: 0 }), {})
          : {};
        setEditingYear(data.year);
        setBlendedFormData({
          year: data.year,
          costs: data.costs && Object.keys(data.costs).length > 0 ? data.costs : fallbackCosts,
          blendPercentages: data.blendPercentages && Object.keys(data.blendPercentages).length > 0 ? data.blendPercentages : fallbackBlend,
          currency: data.currency || 'CAD',
          unitOfMeasure: data.unitOfMeasure || defaultSystemSettings.uom,
          isActive: data.isActive !== undefined ? data.isActive : true
        });
        setShowBlendedModal(true);
      }
    } catch (err) {
      setBlendedError('Failed to load blended costs for editing');
      console.error('Error loading blended costs:', err);
    }
  };

  const handleBlendedDelete = async (yearData) => {
    if (window.confirm(`Delete all blended costs for year ${yearData.year}?`)) {
      try {
        await api.delete(`/blended-surface-prep/year/${yearData.year}`);
        fetchBlendedCosts();
      } catch (err) {
        setBlendedError(err.response?.data?.message || 'Failed to delete');
      }
    }
  };

  const parseBlendPercentages = (json) => {
    try {
      const obj = typeof json === 'string' ? JSON.parse(json) : json;
      return Object.entries(obj).map(([method, pct]) => `${method}: ${pct}%`).join(', ');
    } catch {
      return 'N/A';
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Surface Preparation Methods</h1>
        <p className="text-gray-600">Manage surface preparation methods and blended costs</p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('methods')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'methods'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Methods
          </button>
          <button
            onClick={() => setActiveTab('blended')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'blended'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Blended Costs
          </button>
        </nav>
      </div>

      {/* Error Message */}
      {(error || blendedError) && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {activeTab === 'methods' ? error : blendedError}
        </div>
      )}

      {/* Methods Tab Content */}
      {activeTab === 'methods' && (
        <>
      {/* Filters and Search */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search methods..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={yearFilter}
          onChange={(e) => setYearFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Years</option>
          <option value="2024">2024</option>
          <option value="2025">2025</option>
        </select>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2"
        >
          <FontAwesomeIcon icon={faPlus} className="h-4 w-4" />
          Add Method
        </button>
      </div>

      {/* Table - Grouped by Year */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading methods...</p>
          </div>
        ) : methods.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <p>No surface preparation methods found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12"></th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Year
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Currency / UOM
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Methods Count
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {methods.map((yearData) => {
                  const isExpanded = expandedYears.has(yearData.year);
                  return (
                    <React.Fragment key={yearData.year}>
                      {/* Year Row */}
                      <tr className="hover:bg-gray-50 bg-gray-100">
                        <td className="px-4 py-3">
                          <button
                            onClick={() => toggleYearExpansion(yearData.year)}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            <FontAwesomeIcon 
                              icon={isExpanded ? faChevronDown : faChevronRight} 
                              className="h-4 w-4"
                            />
                          </button>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm font-bold text-gray-900">
                          {yearData.year}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                          {yearData.currency} / {yearData.unitOfMeasure}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                          {yearData.methods.length} method{yearData.methods.length !== 1 ? 's' : ''}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                setEditingYear(yearData.year);
                                setFormData({
                                  method_code: '',
                                  method_name: '',
                                  cost_per_unit: '',
                                  currency: yearData.currency,
                                  unit_of_measure: yearData.unitOfMeasure,
                                  year: yearData.year,
                                  sort_order: 99.0,
                                });
                                setEditingMethod(null);
                                setShowModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-900"
                              title="Add method to this year"
                            >
                              <FontAwesomeIcon icon={faPlus} />
                            </button>
                            <button
                              onClick={() => handleImportPreviousYear(yearData.year)}
                              className="text-green-600 hover:text-green-900 text-xs px-2 py-1 border border-green-300 rounded"
                              title="Import methods from previous year"
                            >
                              Import
                            </button>
                          </div>
                        </td>
                      </tr>
                      {/* Expanded Methods Rows */}
                      {isExpanded && (
                        <>
                          {/* Sub-header for methods */}
                          <tr className="bg-gray-50 border-t-2 border-gray-300">
                            <td className="px-4 py-2"></td>
                            <td colSpan="4" className="px-6 py-2 text-xs font-semibold text-gray-600 uppercase">
                              Methods for {yearData.year}
                            </td>
                          </tr>
                          {/* Method rows */}
                          {yearData.methods.map((method) => (
                            <tr key={method.id} className="hover:bg-gray-50 bg-white">
                              <td className="px-4 py-2"></td>
                              <td className="px-6 py-2 whitespace-nowrap text-sm">
                                <div className="flex flex-col">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium w-fit ${
                                    method.methodCode === 'Default' 
                                      ? 'bg-gray-100 text-gray-800'
                                      : 'bg-blue-100 text-blue-800'
                                  }`}>
                                    {method.methodCode}
                                  </span>
                                  <span className="text-xs text-gray-500 mt-1">{method.methodName}</span>
                                </div>
                              </td>
                              <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-700">
                                <div className="flex items-center">
                                  <FontAwesomeIcon icon={faDollarSign} className="h-3 w-3 text-green-600 mr-1" />
                                  <span className="font-medium">{parseFloat(method.costPerUnit !== undefined ? method.costPerUnit : (method.costPerUnitCad || 0)).toFixed(2)}</span>
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  {method.currency} / {method.unitOfMeasure}
                                </div>
                              </td>
                              <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-700">
                                <div className="flex flex-col">
                                  <span className="text-xs">Sort: {method.sortOrder}</span>
                                  {method.isActive ? (
                                    <span className="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 w-fit">
                                      <FontAwesomeIcon icon={faEye} className="h-3 w-3 mr-1" />
                                      Active
                                    </span>
                                  ) : (
                                    <span className="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 w-fit">
                                      <FontAwesomeIcon icon={faEyeSlash} className="h-3 w-3 mr-1" />
                                      Inactive
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-2 whitespace-nowrap text-sm font-medium">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => handleEdit(method)}
                                    className="text-blue-600 hover:text-blue-900"
                                    title="Edit method"
                                  >
                                    <FontAwesomeIcon icon={faPencil} />
                                  </button>
                                  <button
                                    onClick={() => handleDelete(method.id)}
                                    className="text-red-600 hover:text-red-900"
                                    title="Delete method"
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

        {/* Pagination - Hidden for grouped view */}
        {false && pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                disabled={pagination.page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                disabled={pagination.page === pagination.pages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">
                    {(pagination.page - 1) * pagination.limit + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(pagination.page * pagination.limit, pagination.total)}
                  </span>{' '}
                  of <span className="font-medium">{pagination.total}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                    disabled={pagination.page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setPagination({ ...pagination, page })}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === pagination.page
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                    disabled={pagination.page === pagination.pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
        </>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingMethod ? 'Edit Surface Preparation Method' : 'Add New Surface Preparation Method'}
              </h3>
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Method Code *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.method_code}
                    onChange={(e) => setFormData({ ...formData, method_code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., SP-2, SP-3, Default"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Method Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.method_name}
                    onChange={(e) => setFormData({ ...formData, method_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., SP-2 Surface Preparation"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cost per Unit {formData.currency && formData.unit_of_measure ? `(${formData.currency}/${formData.unit_of_measure === 'sqm' ? 'm²' : 'ft²'})` : ''} *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={formData.cost_per_unit}
                    onChange={(e) => setFormData({ ...formData, cost_per_unit: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Currency *
                  </label>
                  <select
                    required
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="CAD">CAD</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unit of Measure *
                  </label>
                  <select
                    required
                    value={formData.unit_of_measure}
                    onChange={(e) => setFormData({ ...formData, unit_of_measure: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="sqft">sqft (Square Feet)</option>
                    <option value="sqm">sqm (Square Meters)</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Converted Cost ({defaultSystemSettings.currency}/{defaultSystemSettings.uom}) <span className="text-xs text-blue-600">(Read-only)</span>
                  </label>
                  <input
                    type="text"
                    value={convertedCost || ''}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
                    placeholder="Auto-calculated"
                  />
                  <p className="text-xs text-gray-500 mt-1">Automatically converted to default system currency and UOM</p>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sort Order *
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    required
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: parseFloat(e.target.value) || 99.0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 1.0, 1.5, 2.0"
                  />
                  <p className="text-xs text-gray-500 mt-1">Lower numbers appear first. Decimals allowed for flexible ordering.</p>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Year *
                  </label>
                  <input
                    type="number"
                    required
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) || 2024 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="2024"
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {editingMethod ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Blended Costs Tab Content */}
      {activeTab === 'blended' && (
        <>
          {/* Filters */}
          <div className="bg-white p-4 rounded-lg shadow-sm border mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                <input
                  type="number"
                  name="year"
                  value={blendedFilters.year}
                  onChange={handleBlendedFilterChange}
                  placeholder="Filter by year..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Combination</label>
                <select
                  name="combination_code"
                  value={blendedFilters.combination_code}
                  onChange={handleBlendedFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Combinations</option>
                  {combinations.map(combo => (
                    <option key={combo.id} value={combo.code}>
                      {combo.code}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  name="is_active"
                  value={blendedFilters.is_active}
                  onChange={handleBlendedFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={() => {
                    setBlendedFilters({ year: '', is_active: '' });
                    setBlendedPagination(prev => ({ ...prev, currentPage: 1 }));
                  }}
                  className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>

          {/* Add Button */}
          <div className="mb-4">
            <button
              onClick={() => {
                resetBlendedForm();
                setShowBlendedModal(true);
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Add Blended Costs (Year)
            </button>
          </div>

          {/* Table - One row per year with 9 combination columns */}
          <div className="bg-white shadow-sm rounded-lg border overflow-hidden">
            {blendedLoading ? (
              <div className="p-8 text-center text-gray-500">Loading...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                        Year
                      </th>
                      {combinationCodes.map(code => (
                        <th key={code} className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">{code}</th>
                      ))}
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">UOM</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {blendedCosts.map((yearData) => (
                      <tr key={yearData.year} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white z-10">
                          {yearData.year}
                        </td>
                        {combinationCodes.map(code => (
                          <td key={code} className="px-3 py-4 whitespace-nowrap text-center">
                            <div className="text-sm font-medium text-gray-900">
                              ${yearData.costs && yearData.costs[code] != null ? parseFloat(yearData.costs[code]).toFixed(2) : '0.00'}
                            </div>
                          </td>
                        ))}
                        <td className="px-4 py-4 whitespace-nowrap text-center text-sm text-gray-500">
                          {yearData.unitOfMeasure || 'sqm'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-center">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            yearData.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {yearData.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleBlendedEdit(yearData)}
                            className="text-blue-600 hover:text-blue-900 mr-3"
                            title="Edit"
                          >
                            <FontAwesomeIcon icon={faPencil} />
                          </button>
                          <button
                            onClick={() => handleBlendedDelete(yearData)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {blendedPagination.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Page {blendedPagination.currentPage} of {blendedPagination.totalPages}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setBlendedPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                  disabled={!blendedPagination.hasPrevPage}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setBlendedPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                  disabled={!blendedPagination.hasNextPage}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {/* Blended Costs Modal */}
          {showBlendedModal && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
              <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white">
                <div className="mt-3">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {editingYear ? `Edit Blended Costs for Year ${editingYear}` : 'Add New Blended Costs (Year)'}
                  </h3>

                  {blendedError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-red-800 text-sm">{blendedError}</p>
                    </div>
                  )}

                  <form onSubmit={handleBlendedSubmit} className="space-y-4">
                    {/* Year, Currency, UOM */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Year *
                        </label>
                        <input
                          type="number"
                          value={blendedFormData.year}
                          onChange={(e) => {
                            const newYear = parseInt(e.target.value);
                            setBlendedFormData({ ...blendedFormData, year: newYear });
                          }}
                          required
                          disabled={!!editingYear}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Currency *
                        </label>
                        <select
                          value={blendedFormData.currency}
                          onChange={(e) => setBlendedFormData({ ...blendedFormData, currency: e.target.value })}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="CAD">CAD</option>
                          <option value="USD">USD</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Unit of Measure *
                        </label>
                        <select
                          value={blendedFormData.unitOfMeasure}
                          onChange={(e) => setBlendedFormData({ ...blendedFormData, unitOfMeasure: e.target.value })}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="sqft">sqft</option>
                          <option value="sqm">sqm</option>
                        </select>
                      </div>
                    </div>

                    {/* Blended Costs: one input per combination (dynamic from API) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Blended Costs ({blendedFormData.currency}/{blendedFormData.unitOfMeasure}) *
                      </label>
                      {combinationCodes.length === 0 ? (
                        <p className="text-sm text-gray-500">No degradation combinations configured. Add combinations in Corrosion & Damage masters first.</p>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                          {combinationCodes.map(code => (
                            <div key={code}>
                              <label className="block text-xs font-medium text-gray-600 mb-1">{code}</label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={blendedFormData.costs[code] ?? ''}
                                onChange={(e) => setBlendedFormData({
                                  ...blendedFormData,
                                  costs: {
                                    ...blendedFormData.costs,
                                    [code]: e.target.value === '' ? '' : parseFloat(e.target.value) || 0
                                  }
                                })}
                                className="w-full px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="0.00"
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* SP Method Mix: one input per method (dynamic from surface prep methods) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        SP Method Mix (percentages should total 100%)
                      </label>
                      {spMethodCodes.length === 0 ? (
                        <p className="text-sm text-gray-500">No surface prep methods found. Add methods in the Methods tab first.</p>
                      ) : (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {spMethodCodes.map(method => (
                            <div key={method}>
                              <label className="block text-xs text-gray-600 mb-1">{method}</label>
                              <div className="flex items-center">
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="1"
                                  value={blendedFormData.blendPercentages[method] ?? ''}
                                  onChange={(e) => {
                                    const newPercentages = {
                                      ...blendedFormData.blendPercentages,
                                      [method]: e.target.value === '' ? '' : (parseInt(e.target.value, 10) || 0)
                                    };
                                    setBlendedFormData({
                                      ...blendedFormData,
                                      blendPercentages: newPercentages
                                    });
                                  }}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="0"
                                />
                                <span className="ml-1 text-xs text-gray-500">%</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-gray-500 mt-2">
                        Total: {Object.values(blendedFormData.blendPercentages).reduce((sum, val) => sum + (parseInt(val) || 0), 0)}%
                        {Object.values(blendedFormData.blendPercentages).reduce((sum, val) => sum + (parseInt(val) || 0), 0) !== 100 && (
                          <span className="text-orange-600 ml-2">⚠️ Should total 100%</span>
                        )}
                      </p>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={blendedFormData.isActive}
                        onChange={(e) => setBlendedFormData({ ...blendedFormData, isActive: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 text-sm text-gray-700">Active</label>
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t">
                      <button
                        type="button"
                        onClick={() => {
                          setShowBlendedModal(false);
                          resetBlendedForm();
                        }}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={blendedLoading}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                      >
                        {blendedLoading ? 'Saving...' : editingYear ? 'Update' : 'Create'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SurfacePrepMethodsManagement;
